import { AfterViewInit, Component, OnChanges, SimpleChanges, ChangeDetectionStrategy, input } from '@angular/core';
import {CoilSelectionCriteria} from '../../model/coil-selection-criteria';
import {FluidType} from '../../model/fluid-type';
import {UnitConversionUtils} from '../../configurator/unit-conversion-utils';
import {SelectionItem} from '../../selections/selection-item';
import {StringUtils} from '../../utils/string-utils';
import {ConditionValueLimit} from '../../model/condition-value-limit';
import {FormsModule} from '@angular/forms';


@Component({
    selector: 'app-cooling-performance-inputs',
    templateUrl: './cooling-performance-inputs.component.html',
    styleUrls: ['./cooling-performance-inputs.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule]
})
export class CoolingPerformanceInputsComponent implements OnChanges, AfterViewInit {

  readonly criteriaInput = input.required<CoilSelectionCriteria>({ alias: 'criteria' });
  readonly fluidTypes = input.required<FluidType[]>();
  readonly metricInput = input.required<boolean>();
  readonly isRefrigerant = input.required<boolean>();
  readonly invalidCapacity = input.required<boolean>();
  readonly conditionLimits = input.required<ConditionValueLimit[]>();
  readonly airflowInput = input.required<number>({ alias: 'airflow' });
  private airflow!: number;
  deltaValue!: number;
  deltaType: string;
  targetTypes: SelectionItem[];

  constructor() {
    this.deltaType = 'tempChange';
    this.targetTypes = this.getTargetTypes();
  }

  ngAfterViewInit(): void {
    if (this.metricInput()) {
      this.convertConditionalValues();
    }
    this.criteriaInput().targetType = 'LEAVING_AIR_TEMPERATURE';
    this.targetTypeChanged();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['airflowInput']) {
      this.airflow = this.airflowInput();
    }
    if (changes['metricInput'] && !changes['metricInput'].firstChange) {
      if ('tempChange' === this.deltaType) {
        this.criteriaInput().temperatureChange = this.metricInput() ? 5.6 : 10;
        this.deltaValue = this.criteriaInput().temperatureChange;
        this.criteriaInput().fluidFlowRate = undefined!;
      } else {
        this.criteriaInput().fluidFlowRate = this.metricInput() ? 0.2 : 3;
        this.deltaValue = this.criteriaInput().fluidFlowRate;
        this.criteriaInput().temperatureChange = undefined!;
      }
      this.convertConditionalValues();
    }
    if (changes['criteriaInput']?.firstChange) {
      this.deltaValue = this.criteriaInput().temperatureChange;
      this.criteriaInput().fluidFlowRate = undefined!;
    }
  }

  getCoolingDeltaUnits(): string {
    return 'tempChange' === this.deltaType
      ? UnitConversionUtils.getTemperatureUnits(this.metricInput())
      : UnitConversionUtils.getFlowRateUnits(this.metricInput());
  }

  getTemperatureUnits(): string {
    return UnitConversionUtils.getTemperatureUnits(this.metricInput());
  }

  isGlycol(fluidType: string): boolean {
    return 'E' === fluidType || 'P' === fluidType;
  }

  deltaChanged(): void {
    this.criteriaInput().temperatureChange = undefined!;
    this.criteriaInput().fluidFlowRate = undefined!;
    if ('tempChange' === this.deltaType) {
      this.criteriaInput().temperatureChange = this.deltaValue;
    } else {
      this.criteriaInput().fluidFlowRate = this.deltaValue;
    }
  }

  targetTypeChanged(): void {
    this.criteriaInput().targetValue = undefined!;
    if ('AIRFLOW' === this.criteriaInput().targetType && StringUtils.isEmptyNumericValue(this.criteriaInput().targetValue)) {
      this.criteriaInput().targetValue = this.airflow;
    }
  }

  getTargetTypeUnits(): string {
    switch (this.criteriaInput().targetType) {
      case 'LEAVING_AIR_TEMPERATURE':
        return UnitConversionUtils.getTemperatureUnits(this.metricInput());
      case 'TOTAL_CAPACITY':
      case 'SENSIBLE_CAPACITY':
        return UnitConversionUtils.getCapacityUnits(this.metricInput());
      case 'AIRFLOW':
        return UnitConversionUtils.getAirflowUnits(this.metricInput());
    }
    return undefined!;
  }

  private convertConditionalValues(): void {
    this.airflow = this.convertAndRoundAirflow(this.airflow);
    this.criteriaInput().dryBulbTemperature = this.convertAndRoundTemperature(this.criteriaInput().dryBulbTemperature);
    this.criteriaInput().wetBulbTemperature = this.convertAndRoundTemperature(this.criteriaInput().wetBulbTemperature);
    this.criteriaInput().fluidTemperature = this.convertAndRoundTemperature(this.criteriaInput().fluidTemperature);
    switch (this.criteriaInput().targetType) {
      case 'LEAVING_AIR_TEMPERATURE':
        this.criteriaInput().targetValue = this.convertAndRoundTemperature(this.criteriaInput().targetValue);
        break;
      case 'TOTAL_CAPACITY':
      case 'SENSIBLE_CAPACITY':
        this.criteriaInput().targetValue = this.convertAndRoundCapacity(this.criteriaInput().targetValue);
        break;
      case 'AIRFLOW':
        this.criteriaInput().targetValue = this.convertAndRoundAirflow(this.criteriaInput().targetValue);
    }
  }

  private convertAndRoundCapacity(input: number): number {
    if (isNaN(input)) {
      return undefined!;
    }
    return this.round(UnitConversionUtils.convertCapacityValue(input, this.metricInput()), 3);
  }

  private convertAndRoundAirflow(input: number): number {
    if (StringUtils.isEmptyNumericValue(input)) {
      return undefined!;
    }
    return this.round(UnitConversionUtils.convertAirflow(input, this.metricInput()), 3);
  }

  private convertAndRoundTemperature(input: number): number {
    if (isNaN(input)) {
      return undefined!;
    }
    return this.round(UnitConversionUtils.convertTemperatureValue(input, this.metricInput()), 3);
  }

  private round(value: string, digits: number): number {
    const numericValue = +value;
    const roundedValue = numericValue.toFixed(digits);
    return +roundedValue;
  }

  private getTargetTypes(): SelectionItem[] {
    const items: SelectionItem[] = [];
    items.push(this.buildSelectionItem('LEAVING_AIR_TEMPERATURE', 'Leaving Air Temp'));
    items.push(this.buildSelectionItem('TOTAL_CAPACITY', 'Total Capacity'));
    items.push(this.buildSelectionItem('SENSIBLE_CAPACITY', 'SensibleCapacity'));
    items.push(this.buildSelectionItem('AIRFLOW', 'Airflow'));
    return items;
  }

  private buildSelectionItem(value: string, description: string): SelectionItem {
    const item = new SelectionItem();
    item.code = value;
    item.description = description;
    return item;
  }

  isFieldInvalid(fieldName: string): boolean {
    let limits: ConditionValueLimit[] = [];
    switch (fieldName) {
      case 'FluidTemperature':
        if (StringUtils.isZeroOrEmpty(this.criteriaInput().fluidTemperature)) {
          return true;
        }
        if (!this.isRefrigerant()) {
          if (!StringUtils.isZeroOrEmpty(this.criteriaInput().dryBulbTemperature)) {
            if (this.isGlycol(this.criteriaInput().fluidType)) {
              if (this.criteriaInput().dryBulbTemperature <= this.criteriaInput().fluidTemperature) {
                return true;
              }
            } else if (!this.isRefrigerant()) {
              if (this.criteriaInput().dryBulbTemperature - 15 < this.criteriaInput().fluidTemperature) {
                return true;
              }
            }
          }
        }
        return false;
      case 'DryBulbTemperature':
        if (StringUtils.isZeroOrEmpty(this.criteriaInput().dryBulbTemperature)) {
          return true;
        }
        if (!this.isRefrigerant()) {
          if (!StringUtils.isZeroOrEmpty(this.criteriaInput().fluidTemperature)) {
            if (this.isGlycol(this.criteriaInput().fluidType)) {
              if (this.criteriaInput().dryBulbTemperature <= this.criteriaInput().fluidTemperature) {
                return true;
              }
            } else if (!this.isRefrigerant()) {
              if (this.criteriaInput().dryBulbTemperature - 15 < this.criteriaInput().fluidTemperature) {
                return true;
              }
            }
          }
        }
        limits = this.conditionLimits().filter(limit => limit.coolingApplication && limit.fluidTypeCode === this.criteriaInput().fluidType && limit.description.endsWith('Dry Bulb Temperature'));
        if (limits.length > 0) {
          const limit = limits[0];
          if (this.criteriaInput().dryBulbTemperature < limit.minValue || this.criteriaInput().dryBulbTemperature > limit.maxValue) {
            return true;
          }
        }
        return false;
      case 'WetBulbTemperature':
        if (StringUtils.isZeroOrEmpty(this.criteriaInput().wetBulbTemperature)) {
          return true;
        }
        limits = this.conditionLimits().filter(limit => limit.coolingApplication && limit.fluidTypeCode === this.criteriaInput().fluidType && limit.description.endsWith('Wet Bulb Temperature'));
        if (limits.length > 0) {
          const limit = limits[0];
          if (this.criteriaInput().wetBulbTemperature < limit.minValue || this.criteriaInput().wetBulbTemperature > limit.maxValue) {
            return true;
          }
        }
        return false;
      case 'GlycolPercentage':
        if (StringUtils.isZeroOrEmpty(+this.criteriaInput().glycolPercent)) {
          return true;
        }
        limits = this.conditionLimits().filter(limit => limit.coolingApplication && limit.fluidTypeCode === this.criteriaInput().fluidType && limit.description === 'Glycol Percentage');
        if (limits.length > 0) {
          const limit = limits[0];
          if (+this.criteriaInput().glycolPercent < limit.minValue || +this.criteriaInput().glycolPercent > limit.maxValue) {
            return true;
          }
        }
        return false;
      default:
        return false;
    }
  }

}
